import {AfterViewInit, ChangeDetectorRef, Component, forwardRef, SimpleChanges} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AbstractVisbosControl} from "../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {NewBarcodeFormComponent} from "../../../identity/new-barcode-form/new-barcode-form.component";
import {Stock} from "../../../shared/view-model/stock";
import {appInjector} from "../../../index";
import {ProductBatchFormComponent} from "../../product/product-batch-form/product-batch-form.component";
import {DEPOT_YARD_ERRORS} from "../../depot/depot-yard-selection/depot-yard-selection.component";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {StockItemService} from "../../services/stock-item-service/stock-item.service";
import {delay, distinctUntilChanged, filter} from "rxjs/operators";
import {Subject} from "rxjs/Subject";
import {StockItem} from "../../../shared/view-model/store-management/stock-item.type";
import {Observable} from "rxjs/Observable";

@Component({
  selector: 'vb-new-stock-view',
  templateUrl: './new-stock-view.component.html',
  styleUrls: ['./new-stock-view.component.css'],
  providers: [{provide: AbstractVisbosControl, useExisting: forwardRef(() => NewStockViewComponent)}]
})
export class NewStockViewComponent extends AbstractVisbosControl<Stock, FormGroup> implements AfterViewInit {

  searchTerm: Subject<string> = new Subject<string>();
  stockItemsLoading: boolean = false;
  stockItems = [];

  DEPOT_YARD_ERRORS = DEPOT_YARD_ERRORS;

  currentDepotId$: BehaviorSubject<string> = new BehaviorSubject(null);
  currentDepotIdObs$: Observable<string>;
  currentStockItemId$: BehaviorSubject<string> = new BehaviorSubject(null);
  currentCuid$: BehaviorSubject<string> = new BehaviorSubject(null);

  showStockItemSearch: boolean = false;

  batchInit: (fb?: FormBuilder) => {} = ProductBatchFormComponent.initControl;


  private _stockItem: StockItem;

  get stockItem(): StockItem {
    return this._stockItem ? this._stockItem : (this.item ? this.item.stock_item : null);
  }

  set stockItem(value: StockItem) {
    this._stockItem = value;
  }

  constructor(fb: FormBuilder, cd: ChangeDetectorRef, private stockItemService: StockItemService) {
    super(fb, cd, null);
    this.currentDepotIdObs$ = this.currentDepotId$.pipe(filter(v => v != null));
  }


  ngOnInit(): void {
    super.ngOnInit();

    let t = this.searchTerm.pipe(
      delay(500),
      distinctUntilChanged(),
      filter(t => t != null && t != ''),
    );

    this.addSubscription(t.subscribe(t => this.stockItemsLoading = true));
    this.addSubscription(this.stockItemService.findStockItems(t)
      .subscribe(res => {
        this.stockItemsLoading = res.loading;
        this.stockItems = res.data.findStockItems;
      }, err => {
        this.sessionActions.throwError(err);
        this.stockItemsLoading = false;
      }));

    this.addSubscription(this.stockItemService.getStockItem(this.currentStockItemId$.pipe(filter(v => v != null))).subscribe((res: any) => this._stockItem = res));

    this.watchControl();

  }

  private loadStockItem(stockItemCuid) {
    this.currentStockItemId$.next(stockItemCuid);
  }

  onChangeStockItemClicked() {
    this.showStockItemSearch = true;
  }

  search(event) {
    this.searchTerm.next(event.query);
  }

  onSelect(selectedItem) {
    if (selectedItem instanceof Object) {
      this.control.get('stock_item_id').setValue(selectedItem.cuid);
      this.onCancel();
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key == "Escape") {
      this.onCancel();
    }
  }

  onCancel() {
    this.searchTerm.next(null);
    this.stockItems = [];
    this.showStockItemSearch = false;
  }

//TODO: Filter list of depots according to admin unit selected in item form!!

  buildControlContent(): FormGroup {
    return NewStockViewComponent.initControl(this.fb);
  }


  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.control) {
      this.watchControl();
    }
  }


  /**
   * The method is called for changes and in ngOnInit because changes is not triggered, when the control is created internally...
   */
  private watchControl() {
    // Manual triggering is required because valueChanges of form is not a behaviour subject. Does not provide latest value when observers subscribe after a value change
    this.dataSubscriptions.push(this.control.get('depot_id').valueChanges.subscribe(val => this.currentDepotId$.next(val)));
    this.dataSubscriptions.push(this.control.get('cuid').valueChanges.subscribe(val => this.currentCuid$.next(val)));
    this.dataSubscriptions.push(this.control.get('stock_item_id').valueChanges.subscribe(val => this.loadStockItem(val)));

    this.currentCuid$.next(this.control.get('cuid').value);
    this.currentDepotId$.next(this.control.get('depot_id').value);
    this.currentStockItemId$.next(this.control.get('stock_item_id').value);
  }

  public static initControl(fb: FormBuilder = null): FormGroup {
    fb = fb ? fb : appInjector().get(FormBuilder);

    let ctrl = fb.group({
      cuid: [null, Validators.required],
      stock_item_id: [null, Validators.required],
      depot_id: [null, Validators.required],
      depot_yard_id: [null],
      name: [null],
      min_stock: [null, Validators.required],
      report_stock: [null],
      shall_stock: [null, Validators.required],
      act_stock: [null, Validators.required],
      extraction_amount: [null, Validators.required],
      order_amount: [null],
      registration_required: [false],
      ean: NewBarcodeFormComponent.initControl(fb), // Necessary for async validator
      product_batches: fb.array([]),
      keywords: fb.array([]),
      //TODO: Add packing for order amount
    });

    ctrl.get('ean').setValidators(Validators.required);

    return ctrl;

  }

  public setDefaultValues(item?: Stock): { [controlKey: string]: any } | any {
    return {
      cuid: this.setValue(item, 'cuid', AbstractVisbosControl.cuid()),
      stock_item_id: this.setValue(item, 'stock_item.cuid'),
      name: this.setValue(item, 'name'),
      min_stock: this.setValue(item, 'min_stock'),
      report_stock: this.setValue(item, 'report_stock'),
      shall_stock: this.setValue(item, 'shall_stock'),
      act_stock: this.setValue(item, 'act_stock', 0),
      extraction_amount: this.setValue(item, 'extraction_amount', 1),
      order_amount: this.setValue(item, 'order_amount'),
      registration_required: this.setValue(item, 'registration_required', false)
    };
  };

}



// WEBPACK FOOTER //
// ./src/app/store-management/stock/new-stock-view/new-stock-view.component.ts